import { privateApi, publicApi, HMACAuth } from '../config/api';
import API_CONFIG from '../config/api';

class WalletService {
  constructor() {
    this.hmacAuth = null;
    this.initializeHMACAuth();
  }

  initializeHMACAuth() {
    const apiKey = localStorage.getItem('api_key');
    const secretKey = localStorage.getItem('secret_key');
    
    if (apiKey && secretKey) {
      this.hmacAuth = new HMACAuth(apiKey, secretKey);
    }
  }

  // Get user wallets
  async getWallets() {
    try {
      const response = await privateApi.post('/v1/getwallets/');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Create new wallet
  async createWallet(currency) {
    try {
      const response = await privateApi.post('/v1/create_new_wallet/', {
        currency: currency,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Get wallet balances using portfolio balance API with correct prices from external_homepage
  async getBalances() {
    try {
      if (!this.hmacAuth) {
        this.initializeHMACAuth();
      }

      const headers = this.hmacAuth ? this.hmacAuth.getHeaders() : {};
      
      // Fetch portfolio balance data first
      const balanceResponse = await privateApi.get('/v1/portfolio-balance/?currency=USDT', { headers });
      
      // Fetch external homepage prices with error handling
      let externalPrices = {};
      try {
        const externalPricesResponse = await fetch(`${API_CONFIG.BASE_URL}/v1/homepage-external-price/`);
        if (externalPricesResponse.ok) {
          const externalPricesData = await externalPricesResponse.json();
          externalPrices = externalPricesData.pairs_data || {};
        } else {
          console.warn('Failed to fetch external homepage prices, using portfolio balance prices');
        }
      } catch (error) {
        console.warn('Error fetching external homepage prices, using portfolio balance prices:', error);
      }
      
      // Map the API response to the expected format
      const balanceData = balanceResponse.data.balance || {};
      const mappedBalances = {};
      
      // Define coin metadata
      const coinMetadata = {
        BTC: { name: 'BTC', icon: '₿' },
        ETH: { name: 'ETH', icon: 'Ξ' },
        TRX: { name: 'TRX', icon: 'TRX' },
        USDT: { name: 'USDT', icon: '₮' },
        BNB: { name: 'BNB', icon: 'BNB' },
        MATIC: { name: 'Polygon', icon: 'MATIC' },
        BTC1: { name: 'Bitcoin1', icon: '₿1' },
        FTT: { name: 'FTT', icon: 'FTT' }
      };
      
      Object.keys(balanceData).forEach(coin => {
        const balance = balanceData[coin];
        const metadata = coinMetadata[coin] || { name: coin, icon: coin };
        
        // Get correct price from external homepage API
        let currentPrice = parseFloat(balance.price || 0);
        let price24h = parseFloat(balance.price_24h || 0);
        let price24hValue = parseFloat(balance.price_24h_value || 0);
        
        // Override with external homepage prices if available
        const pairKey = `${coin}-USDT`;
        if (externalPrices[pairKey]) {
          const externalPrice = externalPrices[pairKey];
          const oldPrice = currentPrice;
          currentPrice = parseFloat(externalPrice.price || 0);
          price24hValue = parseFloat(externalPrice.price_24h_value || 0);
          // Calculate 24h price from current price and 24h value
          price24h = currentPrice - price24hValue;
          
          // Log price updates for debugging (especially for BTC1)
          if (coin === 'BTC1' || Math.abs(currentPrice - oldPrice) > 0.01) {
            console.log(`Updated ${coin} price from ${oldPrice} to ${currentPrice} using external_homepage API`);
          }
        }
        
        // Calculate USD values using the correct prices
        const actualBalance = parseFloat(balance.actual || 0);
        const ordersBalance = parseFloat(balance.orders || 0);
        const totalBalance = actualBalance + ordersBalance;
        
        // Recalculate USD value using correct price
        const actualUsd = actualBalance * currentPrice;
        const ordersUsd = ordersBalance * currentPrice;
        const totalUsd = totalBalance * currentPrice;
        
        // Calculate 24h change percentage
        let change24h = 0;
        if (price24h > 0 && currentPrice > 0) {
          change24h = ((currentPrice - price24h) / price24h) * 100;
        }
        
        mappedBalances[coin] = {
          coin: coin,
          name: metadata.name,
          total: totalBalance,
          available: actualBalance,
          locked: ordersBalance,
          usdValue: totalUsd, // Use recalculated USD value with correct prices
          change24h: change24h,
          icon: metadata.icon,
          price: currentPrice,
          price24h: price24h,
          // Additional fields for debugging
          actualUsd: actualUsd,
          ordersUsd: ordersUsd,
          externalPriceUsed: !!externalPrices[pairKey]
        };
      });
      
      return mappedBalances;
    } catch (error) {
      console.error('Error fetching portfolio balances:', error);
      throw error.response?.data || error.message;
    }
  }

  // Get specific currency balance
  async getCurrencyBalance(currency) {
    try {
      if (!this.hmacAuth) {
        this.initializeHMACAuth();
      }

      const headers = this.hmacAuth ? this.hmacAuth.getHeaders() : {};
      
      const response = await publicApi.get(`/balance/`, { headers });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Get wallet history
  async getWalletHistory(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.currency) queryParams.append('currency', params.currency);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.offset) queryParams.append('offset', params.offset);
      if (params.operation_type) queryParams.append('operation_type', params.operation_type);

      const url = '/v1/wallet-history/?limit=1000&ordering=-created';
      const response = await privateApi.get(url);
      
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Get deposit address
  async getDepositAddress(currency) {
    try {
      const response = await privateApi.get(`/v1/get_address/`, {
        params: { currency }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Create deposit
  async createDeposit(depositData) {
    try {
      const response = await privateApi.post('/v1/make_deposit/', {
        currency: depositData.currency,
        amount: depositData.amount,
        address: depositData.address,
        tag: depositData.tag || '',
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Create withdrawal
  async createWithdrawal(withdrawalData) {
    try {
      const response = await privateApi.post('/v1/make_withdrawal/', {
        currency: withdrawalData.currency,
        amount: withdrawalData.amount,
        address: withdrawalData.address,
        tag: withdrawalData.tag || '',
        confirmation: withdrawalData.confirmation || false,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Confirm withdrawal
  async confirmWithdrawal(withdrawalId, confirmationCode) {
    try {
      const response = await privateApi.post('/v1/confirm_withdrawal/', {
        withdrawal_id: withdrawalId,
        confirmation_code: confirmationCode,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Cancel withdrawal
  async cancelWithdrawal(withdrawalId) {
    try {
      const response = await privateApi.post('/v1/cancel_withdrawal/', {
        withdrawal_id: withdrawalId,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Get deposits
  async getDeposits(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.currency) queryParams.append('currency', params.currency);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.offset) queryParams.append('offset', params.offset);
      if (params.state) queryParams.append('state', params.state);

      const url = `/v1/deposits/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await privateApi.get(url);
      
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Get withdrawals
  async getWithdrawals(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.currency) queryParams.append('currency', params.currency);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.offset) queryParams.append('offset', params.offset);
      if (params.state) queryParams.append('state', params.state);

      const url = `/v1/withdrawals/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await privateApi.get(url);
      
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Get withdrawal fees
  async getWithdrawalFees() {
    try {
      const response = await privateApi.get('/v1/withdrawal_fees/');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Get supported currencies
  async getSupportedCurrencies() {
    try {
      const response = await privateApi.get('/v1/coins/');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Get currency info
  async getCurrencyInfo(currency) {
    try {
      const response = await privateApi.get(`/v1/currency_info/${currency}/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Get wallet statistics
  async getWalletStats() {
    try {
      const response = await privateApi.get('/v1/wallet_stats/');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Validate address
  async validateAddress(currency, address) {
    try {
      const response = await privateApi.post('/v1/validate_address/', {
        currency: currency,
        address: address,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Get transaction details
  async getTransactionDetails(txId) {
    try {
      const response = await privateApi.get(`/v1/transaction/${txId}/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Estimate withdrawal fee
  async estimateWithdrawalFee(currency, amount) {
    try {
      const response = await privateApi.post('/v1/estimate_withdrawal_fee/', {
        currency: currency,
        amount: amount,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Internal transfer methods
  async validateRecipient(recipientEmail) {
    try {
      const response = await privateApi.post('/v1/transfer/validate-recipient/', {
        recipient_email: recipientEmail,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  async createInternalTransfer(transferData) {
    try {
      const response = await privateApi.post('/v1/transfer/internal/', {
        recipient_email: transferData.recipientEmail,
        currency: transferData.currency,
        amount: transferData.amount,
        note: transferData.note || '',
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  async getTransferHistory(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.offset) queryParams.append('offset', params.offset);
      if (params.currency) queryParams.append('currency', params.currency);
      if (params.type) queryParams.append('type', params.type);

      const url = `/v1/transfer/history/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await privateApi.get(url);
      
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }
}

export default new WalletService(); 